import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/services';

export type PractitionerSearchPreferenceScreen = 'appointments' | 'encounters';

export type UseGetPractitionerSearchPreferenceParams = {
  screen: PractitionerSearchPreferenceScreen;
};

export type GetPractitionerSearchPreferenceResponse<
  T extends Record<string, any>,
> = {
  key: number;
  search_preference: T[];
};

export const useGetPractitionerSearchPreference = <
  T extends Record<string, any>,
>(
  params: UseGetPractitionerSearchPreferenceParams
) => {
  const { screen } = params;
  return useQuery({
    queryKey: ['practitionerSearchPreference', screen],
    queryFn: () =>
      api
        .post<GetPractitionerSearchPreferenceResponse<T>>(
          '/get_recent_search',
          {
            screen,
          }
        )
        .then(({ data }) => data),
  });
};

export type UseSetPractitionerSearchPreferencePayload<
  T extends Record<string, any>,
> = {
  preferences: T[];
  screen: PractitionerSearchPreferenceScreen;
};

export const useSetPractitionerSearchPreference = <
  T extends Record<string, any>,
>() => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['setPractitionerSearchPreference'],
    mutationFn: (payload: UseSetPractitionerSearchPreferencePayload<T>) => {
      queryClient.setQueriesData<GetPractitionerSearchPreferenceResponse<T>>(
        {
          queryKey: ['practitionerSearchPreference', payload.screen],
        },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            search_preference: payload.preferences,
          };
        }
      );
      return api
        .post<{ message: string }>('/add_recent_search', {
          payload: payload.preferences,
          screen: payload.screen,
        })
        .then(({ data }) => data)
        .catch((e) => {
          queryClient.invalidateQueries({
            queryKey: ['practitionerSearchPreference', payload.screen],
          });
          throw e;
        });
    },
  });
};
